// src/contexts/SocketContext.tsx

import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import io, { Socket } from 'socket.io-client';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Define the context type
interface SocketContextType {
  socket: Socket | null;
  setSocket: React.Dispatch<React.SetStateAction<Socket | null>>;
  /** Set of user IDs of friends who are currently online */
  onlineFriendIds: Set<number>;
  /** Dragon invasion event state */
  dragonInvasionActive: boolean;
  dragonInvasionEndsAt: string | null;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const [onlineFriendIds, setOnlineFriendIds] = useState<Set<number>>(new Set());
  const [dragonInvasionActive, setDragonInvasionActive] = useState(false);
  const [dragonInvasionEndsAt, setDragonInvasionEndsAt] = useState<string | null>(null);

  // Fetch initial invasion state once on mount (public route, no auth needed)
  useEffect(() => {
    axios.get<{ isActive: boolean; endsAt: string | null }>(`${API_BASE}/api/events/dragon-invasion`)
      .then((res) => {
        setDragonInvasionActive(res.data.isActive);
        setDragonInvasionEndsAt(res.data.endsAt);
      })
      .catch(() => { /* non-critical */ });
  }, []);

  useEffect(() => {
    if (user && !socketRef.current) {
      const newSocket = io(API_BASE || window.location.origin, {
        withCredentials: true,
        transports: ['polling', 'websocket'],
        reconnectionDelay: 1000,
        reconnectionDelayMax: 15000,
        timeout: 20000,
      });

      socketRef.current = newSocket;

      newSocket.on('connect', () => {
        setSocket(newSocket);
        newSocket.emit('join_room', user.id);
      });

      // Re-join room after a reconnect (new socket ID, must re-join server-side room)
      newSocket.io.on('reconnect', () => {
        newSocket.emit('join_room', user.id);
      });

      // Track which friends are online, server sends { userId, username, displayName }
      newSocket.on('friendOnline', (data: { userId: number }) => {
        if (data?.userId) {
          setOnlineFriendIds(prev => {
            const next = new Set(prev);
            next.add(data.userId);
            return next;
          });
        }
      });

      newSocket.on('friendOffline', (data: { userId: number }) => {
        if (data?.userId) {
          setOnlineFriendIds(prev => {
            const next = new Set(prev);
            next.delete(data.userId);
            return next;
          });
        }
      });

      newSocket.on('disconnect', (reason) => {
        console.warn('Disconnected from socket. Reason:', reason);
        setOnlineFriendIds(new Set());
      });

      newSocket.on('dragonInvasionStarted', (data: { endsAt: string }) => {
        setDragonInvasionActive(true);
        setDragonInvasionEndsAt(data?.endsAt ?? null);
      });

      newSocket.on('dragonInvasionEnded', () => {
        setDragonInvasionActive(false);
        setDragonInvasionEndsAt(null);
      });

      newSocket.on('error', (error) => {
        console.error('Socket error:', error);
      });

      return () => {
        newSocket.close();
        socketRef.current = null;
        setSocket(null);
        setOnlineFriendIds(new Set());
      };
    }
  }, [user]);

  const value = useMemo(() => ({ socket, setSocket, onlineFriendIds, dragonInvasionActive, dragonInvasionEndsAt }), [socket, onlineFriendIds, dragonInvasionActive, dragonInvasionEndsAt]);

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
